// src/components/admin/reports/EngagementReport.jsx
import React, { useState, useEffect } from 'react';
import { getMembers, fmtDate, MEMBER_INTERESTS } from '../../../api/communityApi';

const parseTs = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
};

const daysSince = (ts) => {
  const d = parseTs(ts);
  if (!d) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const suggestedAction = (days) => {
  if (days === null) return 'Review profile';
  if (days < 30)  return 'Send check-in email';
  if (days < 60)  return 'Personal outreach';
  if (days < 90)  return '60-day re-engagement campaign';
  return '90-day re-engagement or review status';
};

const downloadCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  const keys = Object.keys(data[0]);
  const header = keys.join(',');
  const rows = data.map(row =>
    keys.map(k => `"${String(row[k] ?? '').replace(/"/g, '""')}"`).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const avg = (arr) => arr.length === 0 ? 0 : Math.round(arr.reduce((s, n) => s + n, 0) / arr.length);

export default function EngagementReport({ orgId }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [members,   setMembers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  useEffect(() => {
    loadMembers();
  }, [orgId]);

  const loadMembers = async () => {
    setLoading(true);
    setError('');
    try {
      const m = await getMembers(orgId);
      setMembers(m);
    } catch (e) {
      setError('Failed to load member data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Overview computations ──────────────────────────────────────────────────
  const engDist = { high: 0, medium: 0, low: 0, at_risk: 0 };
  members.forEach(m => {
    const t = m.engagementTier || 'at_risk';
    if (engDist[t] !== undefined) engDist[t]++;
  });

  const avgScore = avg(members.map(m => m.engagementScore || 0));

  // By district
  const districtMap = {};
  members.forEach(m => {
    const d = m.district || 'Unknown';
    if (!districtMap[d]) districtMap[d] = { count: 0, scores: [] };
    districtMap[d].count++;
    districtMap[d].scores.push(m.engagementScore || 0);
  });
  const districtRows = Object.entries(districtMap)
    .map(([district, { count, scores }]) => ({ district, count, avgScore: avg(scores) }))
    .sort((a, b) => b.count - a.count);

  // ── At-Risk ─────────────────────────────────────────────────────────────────
  const atRiskMembers = members
    .filter(m => m.engagementTier === 'at_risk')
    .sort((a, b) => (a.engagementScore || 0) - (b.engagementScore || 0));

  const atRiskCSVData = atRiskMembers.map(m => {
    const days = daysSince(m.lastActiveAt);
    return {
      Name: m.displayName || `${m.firstName || ''} ${m.lastName || ''}`.trim(),
      Email: m.email || '',
      Score: m.engagementScore || 0,
      'Last Active': fmtDate(m.lastActiveAt),
      'Days Inactive': days ?? 'Unknown',
      'Suggested Action': suggestedAction(days),
    };
  });

  // ── Segment Analysis ────────────────────────────────────────────────────────
  // By widowStage
  const stageMap = {};
  members.forEach(m => {
    const s = m.widowStage || 'unknown';
    if (!stageMap[s]) stageMap[s] = { count: 0, scores: [] };
    stageMap[s].count++;
    stageMap[s].scores.push(m.engagementScore || 0);
  });

  // By employmentStatus
  const empMap = {};
  members.forEach(m => {
    const e = m.employmentStatus || 'unknown';
    if (!empMap[e]) empMap[e] = { count: 0, scores: [] };
    empMap[e].count++;
    empMap[e].scores.push(m.engagementScore || 0);
  });

  // By top interests
  const interestCount = {};
  members.forEach(m => {
    (m.interests || []).forEach(interest => {
      interestCount[interest] = (interestCount[interest] || 0) + 1;
    });
  });
  const interestRows = Object.entries(interestCount)
    .map(([key, count]) => ({
      key,
      label: MEMBER_INTERESTS.find(i => i.value === key)?.label || key,
      count
    }))
    .sort((a, b) => b.count - a.count);

  const TIER_CONFIG = [
    { tier: 'high',    label: 'High (≥70)',     dotClass: 'bb-eng-dot-high',    textClass: 'bb-eng-high' },
    { tier: 'medium',  label: 'Medium (40–69)', dotClass: 'bb-eng-dot-medium',  textClass: 'bb-eng-medium' },
    { tier: 'low',     label: 'Low (15–39)',     dotClass: 'bb-eng-dot-low',     textClass: 'bb-eng-low' },
    { tier: 'at_risk', label: 'At-Risk (<15)',   dotClass: 'bb-eng-dot-at_risk', textClass: 'bb-eng-at_risk' },
  ];

  if (loading) {
    return (
      <div className="bb-panel">
        <div className="bb-panel-body" style={{ textAlign: 'center', padding: 40 }}>
          <div className="bb-spinner" />
          <p style={{ marginTop: 12, color: '#9CA3AF' }}>Loading engagement data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bb-panel">
      <div className="bb-panel-header">
        <h3 className="bb-panel-title">Engagement Report</h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
          Detailed member engagement analysis across {members.length} members.
        </p>
      </div>

      {error && <div className="bb-alert bb-alert-error" style={{ margin: '0 20px 12px' }}>{error}</div>}

      {/* Sub-tabs */}
      <div className="bb-rpt-subtabs" style={{ margin: '0 20px' }}>
        {['overview', 'at_risk', 'segments'].map(tab => (
          <button
            key={tab}
            className={`bb-rpt-subtab${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' ? 'Overview' : tab === 'at_risk' ? 'At-Risk Members' : 'Segment Analysis'}
            {tab === 'at_risk' && atRiskMembers.length > 0 && (
              <span style={{
                marginLeft: 6, background: '#EF4444', color: '#fff',
                fontSize: 10, padding: '1px 6px', borderRadius: 10, fontWeight: 700
              }}>{atRiskMembers.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="bb-panel-body">

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div>
            {/* Engagement distribution */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
              {TIER_CONFIG.map(({ tier, label, dotClass, textClass }) => (
                <div key={tier} className="bb-rpt-stat">
                  <div className="bb-rpt-stat-val" style={{ fontSize: 24 }}>
                    <span className={dotClass} />
                    <span className={textClass}>{engDist[tier] || 0}</span>
                  </div>
                  <div className="bb-rpt-stat-lbl">{label}</div>
                </div>
              ))}
            </div>

            {/* Average score */}
            <div style={{ background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: 16, marginBottom: 24 }}>
              <div style={{ fontSize: 13, color: '#4338CA', fontWeight: 600 }}>Average Engagement Score</div>
              <div style={{ fontSize: 36, fontWeight: 800, color: '#4338CA', marginTop: 4 }}>{avgScore}</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                across {members.length} members
              </div>
            </div>

            {/* Scoring methodology */}
            <div style={{
              background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
              padding: '12px 16px', marginBottom: 24, fontSize: 12, color: '#78350F'
            }}>
              <strong>Scoring methodology:</strong> Scores calculated from event attendance (50%), recency (30%), programme participation (20%)
            </div>

            {/* By district */}
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
              Members by District
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table className="bb-table">
                <thead>
                  <tr>
                    <th>District</th>
                    <th>Members</th>
                    <th>Avg Engagement Score</th>
                  </tr>
                </thead>
                <tbody>
                  {districtRows.map(row => (
                    <tr key={row.district}>
                      <td style={{ fontWeight: 500 }}>{row.district}</td>
                      <td>{row.count}</td>
                      <td>
                        <span style={{
                          fontWeight: 600,
                          color: row.avgScore >= 70 ? '#065F46' : row.avgScore >= 40 ? '#1E40AF' : row.avgScore >= 15 ? '#92400E' : '#991B1B'
                        }}>
                          {row.avgScore}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {districtRows.length === 0 && (
                    <tr><td colSpan={3} style={{ textAlign: 'center', color: '#9CA3AF' }}>No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── AT-RISK TAB ── */}
        {activeTab === 'at_risk' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: '#6B7280' }}>
                {atRiskMembers.length} member{atRiskMembers.length !== 1 ? 's' : ''} identified as at-risk (engagement score below 15).
              </p>
              {atRiskMembers.length > 0 && (
                <button
                  className="bb-btn bb-btn-secondary"
                  onClick={() => downloadCSV(atRiskCSVData, 'at-risk-members.csv')}
                >
                  Export At-Risk List CSV
                </button>
              )}
            </div>

            {atRiskMembers.length === 0 ? (
              <div style={{
                background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10,
                padding: 24, textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
                <p style={{ fontSize: 14, color: '#065F46', fontWeight: 600 }}>No at-risk members!</p>
                <p style={{ fontSize: 13, color: '#065F46' }}>All members have an engagement score of 15 or above.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Score</th>
                      <th>Last Active</th>
                      <th>Days Inactive</th>
                      <th>Suggested Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atRiskMembers.map(m => {
                      const days   = daysSince(m.lastActiveAt);
                      const action = suggestedAction(days);
                      const name   = m.displayName || `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'Unknown';
                      return (
                        <tr key={m.id}>
                          <td style={{ fontWeight: 500 }}>{name}</td>
                          <td style={{ fontSize: 12, color: '#6B7280' }}>{m.email || '—'}</td>
                          <td>
                            <span className="bb-eng-at_risk" style={{ fontWeight: 700 }}>
                              {m.engagementScore || 0}
                            </span>
                          </td>
                          <td>{fmtDate(m.lastActiveAt)}</td>
                          <td>
                            <span style={{
                              fontWeight: 600,
                              color: days === null ? '#6B7280' : days > 90 ? '#991B1B' : days > 60 ? '#92400E' : '#374151'
                            }}>
                              {days !== null ? days : '—'}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              fontSize: 11, background: '#FEF3C7', color: '#78350F',
                              padding: '3px 8px', borderRadius: 10, fontWeight: 500
                            }}>
                              {action}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── SEGMENT ANALYSIS TAB ── */}
        {activeTab === 'segments' && (
          <div>
            {/* By Widow Stage */}
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
              Breakdown by Widow Stage
            </h4>
            <table className="bb-table" style={{ marginBottom: 24 }}>
              <thead>
                <tr><th>Stage</th><th>Members</th><th>Avg Score</th></tr>
              </thead>
              <tbody>
                {Object.entries(stageMap).map(([stage, { count, scores }]) => (
                  <tr key={stage}>
                    <td style={{ textTransform: 'capitalize', fontWeight: 500 }}>
                      {stage === 'current' ? 'Current (0–3 years)' : stage === 'past' ? 'Past (3+ years)' : stage}
                    </td>
                    <td>{count}</td>
                    <td style={{ fontWeight: 600, color: '#4338CA' }}>{avg(scores)}</td>
                  </tr>
                ))}
                {Object.keys(stageMap).length === 0 && (
                  <tr><td colSpan={3} style={{ color: '#9CA3AF', textAlign: 'center' }}>No data</td></tr>
                )}
              </tbody>
            </table>

            {/* By Employment Status */}
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
              Breakdown by Employment Status
            </h4>
            <table className="bb-table" style={{ marginBottom: 24 }}>
              <thead>
                <tr><th>Employment Status</th><th>Members</th><th>Avg Score</th></tr>
              </thead>
              <tbody>
                {Object.entries(empMap)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([emp, { count, scores }]) => (
                  <tr key={emp}>
                    <td style={{ textTransform: 'capitalize', fontWeight: 500 }}>
                      {emp.replace(/_/g, ' ')}
                    </td>
                    <td>{count}</td>
                    <td style={{ fontWeight: 600, color: '#4338CA' }}>{avg(scores)}</td>
                  </tr>
                ))}
                {Object.keys(empMap).length === 0 && (
                  <tr><td colSpan={3} style={{ color: '#9CA3AF', textAlign: 'center' }}>No data</td></tr>
                )}
              </tbody>
            </table>

            {/* By District */}
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
              Breakdown by District
            </h4>
            <table className="bb-table" style={{ marginBottom: 24 }}>
              <thead>
                <tr><th>District</th><th>Members</th><th>Avg Score</th></tr>
              </thead>
              <tbody>
                {districtRows.map(row => (
                  <tr key={row.district}>
                    <td style={{ fontWeight: 500 }}>{row.district}</td>
                    <td>{row.count}</td>
                    <td style={{ fontWeight: 600, color: '#4338CA' }}>{row.avgScore}</td>
                  </tr>
                ))}
                {districtRows.length === 0 && (
                  <tr><td colSpan={3} style={{ color: '#9CA3AF', textAlign: 'center' }}>No data</td></tr>
                )}
              </tbody>
            </table>

            {/* Top Interests */}
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
              Top Member Interests
            </h4>
            <table className="bb-table">
              <thead>
                <tr><th>Interest</th><th>Members</th></tr>
              </thead>
              <tbody>
                {interestRows.map(row => (
                  <tr key={row.key}>
                    <td style={{ fontWeight: 500 }}>{row.label}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
                {interestRows.length === 0 && (
                  <tr><td colSpan={2} style={{ color: '#9CA3AF', textAlign: 'center' }}>No interest data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
}
